import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import apiServicewt from '../../../api/apiServicewt';
import PhoneNumberInput from '../../../components/common/PhoneNumberInput';
import './Login.css';
import { useLoginSettings } from '../../../contexts/loginSettingsContext';

const GooglePhoneNumber = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const googleUser = location.state?.googleUser;
	const { darkModeStatus } = useDarkMode();
	const { loginSettings, loginSettingsLang } = useLoginSettings();

	const [phoneInputValue, setPhoneInputValue] = useState('');
	const [countryOptions, setCountryOptions] = useState([]);
	const [errorMessage, setErrorMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const rawDefaultCountryCode =
		loginSettings?.permission_settings?.default_country_code ??
		loginSettings?.permission_settings?.registration?.default_country_code;
	const defaultCountryCode = String(rawDefaultCountryCode || '').trim() || '94';
	const loginBgFromSettings = loginSettings?.background_image;
	const defaultLoginBg = `${process.env.PUBLIC_URL}/assets/img/abstract/Main_a.jpg`;
	const loginBg =
		loginBgFromSettings && String(loginBgFromSettings).trim()
			? loginBgFromSettings
			: defaultLoginBg;

	useEffect(() => {
		document.body.classList.add('auth-page');
		return () => document.body.classList.remove('auth-page');
	}, []);

	useEffect(() => {
		if (!googleUser?.email || !googleUser?.google_id) {
			navigate('/login');
		}
	}, [googleUser, navigate]);

	useEffect(() => {
		const countryList = loginSettingsLang?.country_list;
		const phoneCodes = loginSettingsLang?.country_phone_code;

		if (countryList && typeof countryList === 'object') {
			const options = Object.entries(countryList).map(([code, name]) => ({
				code,
				name,
				phoneCode: `+${phoneCodes?.[code] ?? ''}`,
			}));
			setCountryOptions(options);
		}
	}, [loginSettingsLang]);

	useEffect(() => {
		setPhoneInputValue((prev) => prev || `+${defaultCountryCode}`);
	}, [defaultCountryCode]);

	if (!googleUser?.email || !googleUser?.google_id) return null;

	const fullName = googleUser.name?.trim() || '';
	const nameParts = fullName.split(' ');

	const first_name = nameParts[0] || '';
	const middle_name = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
	const last_name = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
	const defaultCountryIso = useMemo(
		() =>
			(
				countryOptions.find((option) => option.phoneCode === `+${defaultCountryCode}`)?.code || ''
			).toLowerCase(),
		[countryOptions, defaultCountryCode],
	);

	const handleContinue = async () => {
		const derivedDialCode = (() => {
			const match = phoneInputValue?.match(/^\+(\d{1,4})/);
			return match ? match[1] : '';
		})();
		const cleanedPhoneNumber = (() => {
			if (!phoneInputValue) return '';
			if (derivedDialCode) {
				return phoneInputValue.replace(`+${derivedDialCode}`, '').replace(/\D/g, '');
			}
			return phoneInputValue.replace(/\D/g, '');
		})();

		if (!derivedDialCode) {
			setErrorMessage('Phone code is required.');
			return;
		}
		if (!cleanedPhoneNumber) {
			setErrorMessage('Phone Number is required.');
			return;
		}
		// if (cleanedPhoneNumber.length < 10) {
		// 	setErrorMessage('Phone Number must be at least 10 digits.');
		// 	return;
		// }

		setErrorMessage('');
		setIsLoading(true);

		try {
			const item = {
				action: 'reg_with_google',
				first_name: first_name,
				middle_name: middle_name,
				last_name: last_name,
				email: googleUser.email,
				phone_code: derivedDialCode,
				phone: cleanedPhoneNumber,
				google_token: googleUser.google_id,
			};

			const result = await apiServicewt(item, navigate);
			const responseType = String(result?.type || '').toLowerCase();

			if (responseType === 'success' || result?.success === true) {
				navigate('/dashboard');
				return;
			}

			setErrorMessage(result?.message || 'Registration failed. Please try again.');
		} catch (error) {
			setErrorMessage(error?.message || 'Could not complete Google signup. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className='bg-img' style={{ backgroundImage: `url(${loginBg})` }}>
			<PageWrapper title='Google Signup'>
				<Page
					className='p-0 container-xxl'
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '100vh',
					}}>
					<Card
						className='shadow-3d-dark'
						style={{
							maxWidth: '400px',
							width: '100%',
						}}>
						<CardBody
							style={{
								justifyContent: 'center',
							}}>
							<div className='text-center my-3'>
								<Link
									to='/'
									className={classNames('text-decoration-none fw-bold display-2', {
										'text-dark': !darkModeStatus,
										'text-light': darkModeStatus,
									})}>
									<Logo width={200} />
								</Link>
							</div>

							<div className='text-center h2 fw-bold mt-3'>Enter Your Phone Number</div>
							<div className='text-center text-muted mt-2 mb-4'>
								Complete your Google signup by adding your phone number.
							</div>

							<div className='mb-3 p-3 rounded border bg-light'>
								<div className='fw-bold'>{googleUser.name}</div>
								<div className='text-muted'>{googleUser.email}</div>
							</div>

							<form
								className='row g-4'
								onSubmit={(event) => {
									event.preventDefault();
									handleContinue();
								}}>
								<div className='col-12'>
									<FormGroup id='google-phone' isFloating>
										<PhoneNumberInput
											value={phoneInputValue}
											defaultCountryIso={defaultCountryIso}
											onChange={(value) => {
												setPhoneInputValue(value);
												setErrorMessage('');
											}}
											inputProps={{
												autoComplete: 'tel',
											}}
										/>
									</FormGroup>
									{errorMessage ? (
										<p style={{ color: 'red', fontSize: '12px' }}>{errorMessage}</p>
									) : null}
								</div>

								<div className='col-12'>
									<Button
										type='submit'
										color='success'
										className='w-100 py-3'
										disabled={isLoading}>
										{isLoading ? 'Please wait...' : 'Continue'}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Page>
			</PageWrapper>
		</div>
	);
};

export default GooglePhoneNumber;
